import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { resolveOrganizationDisplays } from "../services/resolveOrganizationDisplays";
import { listAvailableTeleconsultoria } from "../services/teleconsultoriaScheduleService";
import type { TeleconsultoriaAvailableProfessional } from "../types";

/**
 * Lista de teleconsultores disponíveis + nomes das organizações.
 * `reload` busca de novo sem desmontar o picker.
 */
export function useAvailableTeleconsultoria() {
  const { user } = useSessionContext();
  const [rows, setRows] = useState<TeleconsultoriaAvailableProfessional[]>([]);
  const [loading, setLoading] = useState(false);
  /** true só depois de uma carga bem-sucedida (evita limpar seleção na montagem). */
  const [loaded, setLoaded] = useState(false);
  /** Nome da organização por id, para linhas sem providedBy.display. */
  const [organizationDisplays, setOrganizationDisplays] = useState<
    Map<string, string>
  >(new Map());
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!user?.username) return;

    let cancelled = false;
    setLoading(true);
    setLoaded(false);
    setOrganizationDisplays(new Map());
    listAvailableTeleconsultoria({
      hostPractitionerRef: `Practitioner/${oids.cpf}-${user.username}`,
    })
      .then((list) => {
        if (cancelled) return;
        setRows(list);
        setLoaded(true);
        // Uma resolução por carregamento; nunca rejeita (cai no fallback de CNPJ).
        const missingIds = list
          .filter((row) => !row.organizationDisplay && row.organizationId)
          .map((row) => row.organizationId as string);
        if (!missingIds.length) return;
        void resolveOrganizationDisplays(missingIds).then((displays) => {
          if (!cancelled) setOrganizationDisplays(displays);
        });
      })
      .catch((error) => {
        console.error("TeleconsultoriaProfessionalPicker", error);
        if (!cancelled) {
          toast.error("Não foi possível listar profissionais disponíveis.");
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.username, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return { rows, loading, loaded, organizationDisplays, reload };
}
